import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
  Alert,
  Chip,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  Home,
  Lock,
  CheckCircle,
  Cancel,
  Psychology,
  NavigateNext,
  Replay
} from '@mui/icons-material';
import Dashboard from '../components/Dashboard';
import Header from '../components/Header';
import { selectSelectedStudent } from '../store/selectors/studentSelectors';
import {
  fetchPracticeSet,
  gradePracticeAnswer,
  GradedAnswer,
  PracticeSet,
  PracticeSetResult
} from '../services/practiceService';

/**
 * Adaptive practice (monetization feature #03).
 *
 * Unlike a mock, this grades one question at a time and shows the answer immediately.
 * That is the whole point: practice you cannot learn from is just a quiz. The set arrives
 * without an answer key — it would otherwise be readable in the network tab before
 * answering — so each answer costs a round trip, and the explanation comes back with it.
 *
 * Questions target the student's weakest skills, so the set is not a fixed syllabus and
 * there is no "score" at the end. Progress is the mastery bar, not a mark out of ten.
 */

const PracticePage: React.FC = () => {
  const navigate = useNavigate();
  const selectedStudent = useSelector(selectSelectedStudent);

  const [result, setResult] = useState<PracticeSetResult | null>(null);
  const [index, setIndex] = useState(0);
  const [choice, setChoice] = useState<number | null>(null);
  const [graded, setGraded] = useState<GradedAnswer | null>(null);
  const [grading, setGrading] = useState(false);
  const [gradeError, setGradeError] = useState<string | null>(null);

  /** Wall-clock on the current question, reported with the answer. */
  const startedAtRef = useRef<number>(Date.now());

  useEffect(() => {
    if (!selectedStudent) {
      navigate('/select-student');
    }
  }, [selectedStudent, navigate]);

  const load = useCallback(async () => {
    const studentId = selectedStudent?.id;
    if (!studentId) return;
    setResult(null);
    setIndex(0);
    setChoice(null);
    setGraded(null);
    setGradeError(null);
    startedAtRef.current = Date.now();
    setResult(await fetchPracticeSet(studentId, 10));
  }, [selectedStudent?.id]);

  useEffect(() => {
    load();
  }, [load]);

  if (!selectedStudent) return null;

  const set: PracticeSet | null = result?.state === 'ok' ? result.set : null;
  const item = set?.items[index] ?? null;
  const isLast = set ? index >= set.items.length - 1 : false;

  const handleSubmit = async () => {
    if (!item || choice === null) return;
    setGrading(true);
    setGradeError(null);
    const seconds = Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000));
    const r = await gradePracticeAnswer(selectedStudent.id, item.itemId, choice, seconds);
    setGrading(false);

    if (r.state === 'ok') {
      setGraded(r.graded);
    } else if (r.state === 'locked') {
      // The subscription lapsed mid-set. Fall back to the locked screen rather than
      // leaving the student clicking a button that will never respond.
      setResult({ state: 'locked' });
    } else {
      setGradeError(r.message);
    }
  };

  const handleNext = () => {
    setIndex((i) => i + 1);
    setChoice(null);
    setGraded(null);
    setGradeError(null);
    startedAtRef.current = Date.now();
  };

  const body = (() => {
    if (result === null) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (result.state === 'locked') {
      return (
        <Paper sx={{ p: 4, my: 4, textAlign: 'center' }}>
          <Lock sx={{ fontSize: 44, color: 'text.secondary', mb: 1 }} />
          <Typography variant="h5" sx={{ mb: 1 }}>
            Practise as much as you like
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 2, maxWidth: 460, mx: 'auto' }}
          >
            Unlimited practice questions, chosen to target the skills costing you the most
            marks — with the answer and explanation after every one.
          </Typography>
          {/* Goes to the checkout, which reports honestly when no processor is
              configured rather than pretending to sell something. */}
          <Button
            variant="contained"
            size="large"
            onClick={() => navigate('/checkout?sku=subscription')}
          >
            Unlock practice
          </Button>
        </Paper>
      );
    }

    if (result.state === 'unauthorized') {
      return <Alert severity="warning" sx={{ my: 4 }}>Please sign in again to practise.</Alert>;
    }

    if (result.state === 'error') {
      return (
        <Box sx={{ my: 4 }}>
          <Alert severity="error" sx={{ mb: 2 }}>
            {result.message}
          </Alert>
          <Button variant="outlined" startIcon={<Replay />} onClick={load}>
            Try again
          </Button>
        </Box>
      );
    }

    if (!set || set.items.length === 0) {
      return (
        <Paper sx={{ p: 4, my: 4, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            No practice questions available yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {set?.note ?? 'There are no questions in the bank for you right now.'}
          </Typography>
        </Paper>
      );
    }

    // Finished the set.
    if (!item) {
      return (
        <Paper sx={{ p: 4, my: 4, textAlign: 'center' }}>
          <CheckCircle color="success" sx={{ fontSize: 44, mb: 1 }} />
          <Typography variant="h5" sx={{ mb: 1 }}>
            Set complete
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            That is {set.items.length} questions done. The next set will target whatever is
            still weakest.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Button variant="contained" startIcon={<Replay />} onClick={load}>
              Another set
            </Button>
            <Button variant="outlined" onClick={() => navigate('/progress')}>
              See my progress
            </Button>
          </Box>
        </Paper>
      );
    }

    return (
      <Box sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Psychology color="primary" />
          <Typography variant="h4">Practice</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {set.targetedSkills.length > 0
            ? `Aimed at your weakest areas: ${set.targetedSkills.slice(0, 3).join(', ')}.`
            : 'A starter set — once you have answered a few, these will target your weak areas.'}
        </Typography>

        <LinearProgress
          variant="determinate"
          value={((index + (graded ? 1 : 0)) / set.items.length) * 100}
          sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
        />
        <Typography variant="caption" color="text.secondary">
          Question {index + 1} of {set.items.length}
        </Typography>

        {set.note && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {set.note}
          </Alert>
        )}

        <Paper sx={{ p: 3, mt: 2 }}>
          {item.cognitiveSkill && (
            <Chip size="small" variant="outlined" label={item.cognitiveSkill} sx={{ mb: 2 }} />
          )}

          <Typography variant="h6" sx={{ mb: 2 }}>
            {item.question}
          </Typography>

          <RadioGroup
            value={choice === null ? '' : String(choice)}
            onChange={(e) => setChoice(Number(e.target.value))}
          >
            {item.options.map((opt, i) => {
              const isKey = graded?.correctAnswerIndex === i;
              const isMine = choice === i;
              // Colour only after grading — before that, highlighting anything would
              // give the answer away.
              const borderColor = !graded
                ? 'divider'
                : isKey
                  ? 'success.main'
                  : isMine
                    ? 'error.main'
                    : 'divider';

              return (
                <FormControlLabel
                  key={i}
                  value={String(i)}
                  disabled={!!graded || grading}
                  control={<Radio />}
                  label={opt}
                  sx={{
                    m: 0,
                    mb: 1,
                    px: 1.5,
                    py: 0.5,
                    border: '1px solid',
                    borderColor,
                    borderRadius: 1
                  }}
                />
              );
            })}
          </RadioGroup>

          {gradeError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {gradeError}
            </Alert>
          )}

          {graded && (
            <Box sx={{ mt: 2 }}>
              <Divider sx={{ mb: 2 }} />
              {graded.correct === null ? (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {graded.note ?? 'This question could not be graded.'}
                </Alert>
              ) : (
                <Chip
                  icon={graded.correct ? <CheckCircle /> : <Cancel />}
                  color={graded.correct ? 'success' : 'error'}
                  label={graded.correct ? 'Correct' : 'Not quite'}
                  sx={{ mb: 2 }}
                />
              )}

              {graded.explanation && (
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', mb: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Why
                  </Typography>
                  <Typography variant="body2">{graded.explanation}</Typography>
                </Box>
              )}

              {graded.skillProgress?.masteryPct != null && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {graded.skillProgress.cognitiveSkill}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {graded.skillProgress.correct}/{graded.skillProgress.attempted} ·{' '}
                      {graded.skillProgress.masteryPct.toFixed(0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(100, graded.skillProgress.masteryPct)}
                    sx={{ height: 6, borderRadius: 3 }}
                  />
                </Box>
              )}
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            {!graded ? (
              <Button
                variant="contained"
                disabled={choice === null || grading}
                onClick={handleSubmit}
                startIcon={grading ? <CircularProgress size={16} color="inherit" /> : undefined}
              >
                {grading ? 'Checking…' : 'Check answer'}
              </Button>
            ) : (
              <Button variant="contained" endIcon={<NavigateNext />} onClick={handleNext}>
                {isLast ? 'Finish' : 'Next question'}
              </Button>
            )}
            <Button variant="text" onClick={() => navigate('/dashboard')} startIcon={<Home />}>
              Leave
            </Button>
          </Box>
        </Paper>
      </Box>
    );
  })();

  return (
    <Dashboard
      constrained
      header={<Header selectedStudent={selectedStudent} />}
      students={body}
    />
  );
};

export default PracticePage;
